import { useTranslation } from '@kostbar/i18n'
import type { PeriodPreset } from '@/features/reports/lib/period'
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group'

const PRESETS: Array<Exclude<PeriodPreset, 'custom'>> = [
  'today',
  '7d',
  '14d',
  '30d',
]

interface PeriodPresetsProps {
  value: PeriodPreset
  onChange: (preset: PeriodPreset) => void
}

/**
 * The four period lengths, beside the picker rather than inside it
 * (CHG-2026-09-manager-reporting, restored after CHG-2026-09-smarter-period-picker folded them
 * into the picker's quick list).
 *
 * The argument for folding them in was that "7 T" is a machine's way of saying "letzte Woche" and
 * ends today, so it cuts last week in half. True, and beside the point: nobody clicking `7 T` is
 * asking for last week — they are asking "how are the last seven days going", which is a rolling
 * window and has no natural-language name. Making that cost two clicks was the regression.
 *
 * `Heute` is not a fourth length. It puts the page into its running-day state: hourly trend,
 * forecast tile, and every comparison against the same weekday up to the same time of day.
 *
 * The picker beside this owns everything these four cannot say — a named week, a month, a quarter,
 * a span with times. While one of these is active it names itself ("Zeitraum wählen") instead of
 * restating the period, so the two controls never both claim to be the answer.
 */
export function PeriodPresets({ value, onChange }: PeriodPresetsProps) {
  const { t } = useTranslation('reports')

  return (
    <ToggleGroup
      type="single"
      variant="outline"
      // Empty while a picked range is active, so the picker beside it is visibly the selection
      // rather than a preset appearing to be on at the same time.
      value={value === 'custom' ? '' : value}
      // Radix clears the value when the active item is pressed again; keeping the old one means
      // the page never ends up with no period at all.
      onValueChange={(next) => {
        if (next) onChange(next as PeriodPreset)
      }}
      aria-label={t('period.label')}
    >
      {PRESETS.map((preset) => (
        <ToggleGroupItem key={preset} value={preset} className="h-12 sm:h-9">
          {t(`period.presets.${preset}`)}
        </ToggleGroupItem>
      ))}
    </ToggleGroup>
  )
}
